import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { User, Mail, Phone, MapPin, Calendar, Clock, FileText, AlertTriangle, Navigation, Heart, Activity, Users, Home, Brain, DollarSign, Shield, Edit2, X, Save, Plus, Trash2 } from 'lucide-react';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';

const TIME_SLOTS = [
  { id: 'morning', label: 'Matin', timeRange: '06:00 - 12:00' },
  { id: 'noon', label: 'Midi', timeRange: '12:00 - 14:00' },
  { id: 'afternoon', label: 'Après-midi', timeRange: '14:00 - 18:00' },
  { id: 'evening', label: 'Soir', timeRange: '18:00 - 22:00' },
];

const ClientSummary = ({ client, db, currentUser }) => {
  // Vérification de sécurité pour client null
  if (!client) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6">Résumé du client</h1>
        <div className="text-center p-8">
          <AlertTriangle size={48} className="mx-auto mb-4 text-yellow-500" />
          <h2 className="text-xl font-semibold mb-2">Client non sélectionné</h2>
          <p className="text-gray-600">
            Veuillez sélectionner un client depuis la liste des bénéficiaires pour afficher son résumé.
          </p>
        </div>
      </div>
    );
  }

  const [contract, setContract] = useState(null);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [tasksList, setTasksList] = useState([]);  // All available tasks
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [timeSlotTasks, setTimeSlotTasks] = useState({
    morning: [],
    noon: [],
    afternoon: [],
    evening: [],
  });
  const [employees, setEmployees] = useState([]);
  const [accessibleEmployees, setAccessibleEmployees] = useState([]);
  const [, setGoogleMapsLoaded] = useState(false);

  // États pour l'édition rapide
  const [isEditingHealth, setIsEditingHealth] = useState(false);
  const [healthForm, setHealthForm] = useState({});
  const [isEditingContacts, setIsEditingContacts] = useState(false);
  const [contactsForm, setContactsForm] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Utiliser le nouveau système de permissions avancées
  const { hasPermission: hasAdvancedPermission, loading: permissionsLoading } = useAdvancedPermissions({ userId: currentUser?.id });

  // Définir les permissions avec le nouveau système
  const canViewClients = hasAdvancedPermission({ resource: 'beneficiaries', action: 'read' });
  const canEditClients = hasAdvancedPermission({ resource: 'beneficiaries', action: 'write' });
  const canViewContracts = hasAdvancedPermission({ resource: 'contracts', action: 'read' });
  const canEditContracts = hasAdvancedPermission({ resource: 'contracts', action: 'write' });

  // Charger Google Maps API
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      return !!(window.google && window.google.maps && typeof window.google.maps.Geocoder === 'function');
    };

    if (checkGoogleMapsLoaded()) {
      setGoogleMapsLoaded(true);
      return;
    }

    // Vérifier si l'API est déjà en cours de chargement
    if (document.querySelector('script[data-gmaps]')) {
      const interval = setInterval(() => {
        if (checkGoogleMapsLoaded()) {
          clearInterval(interval);
          setGoogleMapsLoaded(true);
        }
      }, 100);
      return;
    }

    // Charger l'API seulement si la clé est configurée
    const loadGoogleMaps = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists() && settingsDoc.data().googleMapsApiKey) {
          const script = document.createElement('script');
          script.setAttribute('data-gmaps', '1');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${settingsDoc.data().googleMapsApiKey}&libraries=places,geocoding&v=weekly`;
          script.async = true;
          script.defer = true;
          script.onload = () => setGoogleMapsLoaded(true);
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la clé API Google Maps:', error);
      }
    };

    loadGoogleMaps();
  }, [db]);


  // Géocodage adresse → coords avec validation robuste
  const getCoordinatesFromAddress = async (address) => {
    // Validation robuste de l'adresse
    if (!address) {
      console.warn('❌ Adresse vide ou non définie');
      return null;
    }

    // Vérifier que l'adresse est une chaîne de caractères
    if (typeof address !== 'string') {
      console.warn('❌ Adresse n\'est pas une chaîne de caractères:', typeof address, address);
      return null;
    }

    // Nettoyer l'adresse
    const cleanAddress = address.trim();
    if (cleanAddress.length === 0) {
      console.warn('❌ Adresse vide après nettoyage');
      return null;
    }

    // Vérifier que l'adresse contient au moins un caractère alphanumérique
    if (!/[a-zA-Z0-9]/.test(cleanAddress)) {
      console.warn('❌ Adresse ne contient pas de caractères alphanumériques:', cleanAddress);
      return null;
    }

    if (!window.google || !window.google.maps || typeof window.google.maps.Geocoder !== 'function') {
      console.warn('Google Maps Geocoder non disponible');
      return null;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: cleanAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            console.log(`📍 Géocodage réussi pour: "${cleanAddress}"`);
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            console.warn(`❌ Géocodage échoué pour: "${cleanAddress}" - Status: ${status}`);
            reject(new Error(`Adresse non trouvée: ${status}`));
          }
        });
      });
    } catch (e) {
      console.error('Erreur géocodage:', e);
      return null;
    }
  };

  // Fonction pour calculer la distance entre deux coordonnées (formule Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance en km
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!db || !client || !client.id) {
        console.error("La base de données ou les informations du client ne sont pas disponibles");
        return;
      }

      try {
        // Charger les tâches
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        const allTasks = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTasksList(allTasks);

        // Charger le contrat
        const contractDoc = await getDoc(doc(db, 'contracts', client.id));
        if (contractDoc.exists()) {
          const contractData = contractDoc.data();
          setContract(contractData);
          setTimeSlotTasks(contractData.tasksPerPeriod || {});
        }

        // Charger les employés
        const employeesRef = collection(db, 'employees');
        const employeesQuery = query(employeesRef, where('isArchived', '==', false));
        const employeesSnapshot = await getDocs(employeesQuery);
        const employeesData = employeesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          defaultRadius: doc.data().defaultRadius || 10 // Rayon par défaut 10km
        }));
        setEmployees(employeesData);

        // Charger les rendez-vous
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfThreeMonths = new Date(now.getFullYear(), now.getMonth() + 3, 0);

        const appointmentsRef = collection(db, 'appointments');
        const appointmentsQuery = query(
          appointmentsRef,
          where('clientId', '==', client.id),
          where('date', '>=', startOfToday),
          where('date', '<=', endOfThreeMonths)
        );
        const appointmentsSnapshot = await getDocs(appointmentsQuery);
        const appointmentsData = appointmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        let next = null;

        appointmentsData.forEach(app => {
          const appDate = app.date.toDate();
          const [hours, minutes] = app.startTime.split(':').map(Number);
          const appDateTime = new Date(
            appDate.getFullYear(),
            appDate.getMonth(),
            appDate.getDate(),
            hours,
            minutes
          );

          if (appDateTime >= now && (!next || appDateTime < next.dateTime)) {
            next = { ...app, dateTime: appDateTime };
          }
        });

        setNextAppointment(next);

      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      }
    };

    fetchData();
  }, [client, db]);

  // Calculer les employés accessibles quand les données sont chargées
  useEffect(() => {
    const calculateAccessibleEmployees = async () => {
      if (!client?.address || employees.length === 0) return;

      try {
        // Géocoder l'adresse du client
        const clientCoords = await getCoordinatesFromAddress(client.address);
        if (!clientCoords) return;

        const accessible = [];

        for (const employee of employees) {
          if (!employee.address?.street) continue;

          // Construire l'adresse complète de l'employé
          const employeeAddress = `${employee.address.street}, ${employee.address.postalCode} ${employee.address.city}`;

          // Géocoder l'adresse de l'employé
          const employeeCoords = await getCoordinatesFromAddress(employeeAddress);
          if (!employeeCoords) continue;

          // Calculer la distance
          const distance = calculateDistance(
            employeeCoords.lat,
            employeeCoords.lng,
            clientCoords.lat,
            clientCoords.lng
          );

          // Vérifier si le client est dans la zone de l'employé
          const employeeRadius = employee.defaultRadius || 10;
          if (distance <= employeeRadius) {
            accessible.push({
              ...employee,
              distance: Math.round(distance * 10) / 10, // Arrondir à 1 décimale
              inZone: true
            });
          }
        }

        setAccessibleEmployees(accessible);
      } catch (error) {
        console.error("Erreur lors du calcul des employés accessibles:", error);
      }
    };

    calculateAccessibleEmployees();
    calculateAccessibleEmployees();
  }, [client, employees]);

  // Fonctions d'édition
  const handleEditHealth = () => {
    setHealthForm({
      generalHealth: client.healthSummary?.generalHealth || '',
      mainPathologies: client.healthSummary?.mainPathologies || '',
      medicationCount: client.healthSummary?.medicationCount || '',
      fallRisk: client.healthSummary?.fallRisk || '',
      allergies: client.healthSummary?.allergies || '' // Ajout allergies si présent
    });
    setIsEditingHealth(true);
  };

  const saveHealth = async () => {
    setIsSaving(true);
    try {
      const clientRef = doc(db, 'beneficiaries', client.id);
      await updateDoc(clientRef, {
        healthSummary: {
          ...healthForm
        },
        lastUpdate: serverTimestamp()
      });
      setIsEditingHealth(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des infos santé:", error);
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditContacts = () => {
    setContactsForm(client.emergencyContacts ? [...client.emergencyContacts] : []);
    setIsEditingContacts(true);
  };

  const updateContact = (index, field, value) => {
    const updated = [...contactsForm];
    updated[index] = { ...updated[index], [field]: value };
    setContactsForm(updated);
  };

  const addContact = () => {
    setContactsForm([...contactsForm, { name: '', relationship: '', phone1: '', phone2: '' }]);
  };

  const removeContact = (index) => {
    const updated = contactsForm.filter((_, i) => i !== index);
    setContactsForm(updated);
  };

  const saveContacts = async () => {
    setIsSaving(true);
    try {
      const clientRef = doc(db, 'beneficiaries', client.id);
      await updateDoc(clientRef, {
        emergencyContacts: contactsForm,
        lastUpdate: serverTimestamp()
      });
      setIsEditingContacts(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des contacts:", error);
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };


  const handleAddressClick = () => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      // C'est un appareil mobile
      setShowMapOptions(true);
    } else {
      // C'est un PC, ouvrir directement Google Maps
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}`, '_blank');
    }
  };

  const openMaps = (app) => {
    const address = encodeURIComponent(client.address);
    let url;
    switch (app) {
      case 'google':
        url = `https://www.google.com/maps/search/?api=1&query=${address}`;
        break;
      case 'waze':
        url = `https://waze.com/ul?q=${address}`;
        break;
      default:
        url = `maps://?q=${address}`;
    }
    window.open(url, '_blank');
    setShowMapOptions(false);
  };



  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time;
  };



  const getTaskNames = (taskIds) => {
    return taskIds
      .map(id => tasksList.find(task => task.id === id))
      .filter(task => task)
      .map(task => task.name);
  };

  const renderTimeSlotTasks = (slot) => {
    const taskIds = timeSlotTasks[slot.id] || [];
    const taskNames = getTaskNames(taskIds);

    return (
      <div className="space-y-2">
        {taskNames.length > 0 ? (
          taskNames.map((taskName, index) => (
            <span
              key={index}
              className="inline-flex items-center bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full"
            >
              {taskName}
            </span>
          ))
        ) : (
          <p className="text-gray-500 italic">Aucune tâche pour ce créneau</p>
        )}
      </div>
    );
  };


  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };


  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6">Résumé du client</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-semibold mb-4">Informations personnelles</h2>
          <div className="space-y-2">
            <p className="flex items-center"><User className="mr-2" size={18} /> {client.firstName} {client.lastName}</p>
            <p className="flex items-center"><Mail className="mr-2" size={18} /> {client.email}</p>
            <p className="flex items-center"><Phone className="mr-2" size={18} /> {client.phoneNumber}</p>
            <div className="relative">
              <p className="flex items-center">
                <MapPin className="mr-2" size={18} />
                <span className="cursor-pointer text-blue-600 hover:text-blue-800" onClick={handleAddressClick}>
                  {typeof client.address === 'string'
                    ? client.address
                    : `${client.address?.street || ''} ${client.address?.postalCode || ''} ${client.address?.city || ''}`.trim()
                  }
                </span>
                {client.addressNumber && (
                  <span className="ml-2">Apt. {client.addressNumber}</span>
                )}
              </p>
              {showMapOptions && (
                <div className="absolute z-10 mt-2 py-2 w-48 bg-white rounded-md shadow-xl">
                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => openMaps('google')}>Google Maps</button>
                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => openMaps('apple')}>Apple Maps</button>
                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => openMaps('waze')}>Waze</button>
                </div>
              )}
            </div>
          </div>
        </div>
        {(canEditClients && canViewContracts) ? (
          <div>
            <h2 className="text-xl font-semibold mb-4">Détails du contrat</h2>
            {contract ? (
              <div className="space-y-2">
                <p className="flex items-center"><Calendar className="mr-2" size={18} /> Début: {formatDate(contract.startDate)}</p>
                {contract.isOpenEnded ? (
                  <p className="flex items-center"><AlertTriangle className="mr-2" size={18} /> Contrat sans fin</p>
                ) : (
                  <p className="flex items-center"><Calendar className="mr-2" size={18} /> Fin: {formatDate(contract.endDate)}</p>
                )}
                <p className="flex items-center"><Clock className="mr-2" size={18} /> {contract.hoursPerMonth} heures par mois</p>
                <p className="flex items-center"><FileText className="mr-2" size={18} /> Tarif horaire: {contract.hourlyRate}€</p>
              </div>
            ) : (
              <p>Aucun contrat trouvé</p>
            )}
          </div>
        ) : (
          <div>
            <h2 className="text-xl font-semibold mb-4">Détails du contrat</h2>
            {contract ? (
              <div className="space-y-2">
                <p className="flex items-center"><Calendar className="mr-2" size={18} /> Début: {formatDate(contract.startDate)}</p>
                {contract.isOpenEnded ? (
                  <p className="flex items-center"><AlertTriangle className="mr-2" size={18} /> Contrat sans fin</p>
                ) : (
                  <p className="flex items-center"><Calendar className="mr-2" size={18} /> Fin: {formatDate(contract.endDate)}</p>
                )}
                <p className="flex items-center"><Clock className="mr-2" size={18} /> {contract.hoursPerMonth} heures par mois</p>
                <p className="flex items-center"><FileText className="mr-2" size={18} /> Tarif horaire: {contract.hourlyRate}€</p>
              </div>
            ) : (
              <p>Aucun contrat trouvé</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-4">Tâches par créneau horaire</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIME_SLOTS.map((slot) => (
            <div key={slot.id} className="bg-blue-50 p-4 rounded-lg shadow">
              <div className="flex items-center mb-2">
                <Navigation className="mr-2 text-blue-600" size={18} />
                <h3 className="font-bold text-blue-600">{slot.label} ({slot.timeRange})</h3>
              </div>
              {renderTimeSlotTasks(slot)}
            </div>
          ))}
        </div>
      </div>


      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-4">Prochain rendez-vous</h2>
        {nextAppointment ? (
          <div className="bg-blue-100 p-4 rounded-lg">
            <p className="font-semibold">
              {isToday(nextAppointment.dateTime) ? "Aujourd'hui : " : formatDate(nextAppointment.dateTime)}
            </p>
            <p>{formatTime(nextAppointment.startTime)} - {formatTime(nextAppointment.endTime)} - avec {nextAppointment.employeeName} </p>
          </div>
        ) : (
          <p>Aucun rendez-vous prévu</p>
        )}
      </div>

      {/* Section d'évaluation enrichie */}
      {client.assessmentQuestionnaire?.status === 'complété' && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Activity className="mr-2 text-purple-600" size={20} />
            Évaluation d'autonomie
          </h2>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Score d'autonomie */}
              {client.autonomyAssessment?.autonomyScore && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-700">
                    {client.autonomyAssessment.autonomyScore}%
                  </div>
                  <div className="text-sm text-purple-600">Score d'autonomie</div>
                </div>
              )}

              {/* GIR */}
              {client.autonomyAssessment?.gir && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-700">
                    GIR {client.autonomyAssessment.gir}
                  </div>
                  <div className="text-sm text-purple-600">Niveau de dépendance</div>
                </div>
              )}

              {/* Heures estimées */}
              {client.serviceNeeds?.estimatedWeeklyHours && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-700">
                    {client.serviceNeeds.estimatedWeeklyHours}h
                  </div>
                  <div className="text-sm text-purple-600">Heures/semaine</div>
                </div>
              )}

              {/* Date d'évaluation */}
              {client.assessmentQuestionnaire?.completedAt && (
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-700">
                    {formatDate(client.assessmentQuestionnaire.completedAt.toDate())}
                  </div>
                  <div className="text-sm text-purple-600">Évalué le</div>
                </div>
              )}
            </div>

            {/* Domaines nécessitant de l'aide */}
            {client.autonomyAssessment?.needsHelp && (
              <div className="mt-4">
                <h3 className="font-semibold text-purple-800 mb-2">Domaines nécessitant de l'aide:</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(client.autonomyAssessment.needsHelp)
                    .filter(([_, needsHelp]) => needsHelp)
                    .map(([domain]) => (
                      <span key={domain} className="bg-purple-200 text-purple-800 text-sm px-3 py-1 rounded-full">
                        {domain === 'hygiene' && 'Hygiène'}
                        {domain === 'nutrition' && 'Alimentation'}
                        {domain === 'mobility' && 'Mobilité'}
                        {domain === 'housekeeping' && 'Entretien'}
                        {domain === 'domestic' && 'Activités domestiques'}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Section santé */}
      {(client.healthSummary || canEditClients) && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Heart className="mr-2 text-pink-600" size={20} />
              Informations de santé
            </h2>
            {canEditClients && !isEditingHealth && (
              <button
                onClick={handleEditHealth}
                className="text-gray-500 hover:text-pink-600 p-1 rounded-full hover:bg-pink-50 transition-colors"
                title="Modifier les informations de santé"
              >
                <Edit2 size={18} />
              </button>
            )}
          </div>

          {isEditingHealth ? (
            <div className="bg-white p-4 rounded-lg border-2 border-pink-200 shadow-sm">
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">État général</label>
                  <textarea
                    value={healthForm.generalHealth}
                    onChange={(e) => setHealthForm({ ...healthForm, generalHealth: e.target.value })}
                    className="w-full p-2 border rounded-md focus:ring-pink-500 focus:border-pink-500"
                    rows="2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pathologies principales</label>
                  <textarea
                    value={healthForm.mainPathologies}
                    onChange={(e) => setHealthForm({ ...healthForm, mainPathologies: e.target.value })}
                    className="w-full p-2 border rounded-md focus:ring-pink-500 focus:border-pink-500"
                    rows="2"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Médicaments (nb/jour ou détails)</label>
                    <input
                      type="text"
                      value={healthForm.medicationCount}
                      onChange={(e) => setHealthForm({ ...healthForm, medicationCount: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Risque de chute</label>
                    <select
                      value={healthForm.fallRisk}
                      onChange={(e) => setHealthForm({ ...healthForm, fallRisk: e.target.value })}
                      className="w-full p-2 border rounded-md focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="">Non spécifié</option>
                      <option value="Faible">Faible</option>
                      <option value="Moyen">Moyen</option>
                      <option value="Élevé">Élevé</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setIsEditingHealth(false)}
                  className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center"
                  disabled={isSaving}
                >
                  <X size={16} className="mr-1" /> Annuler
                </button>
                <button
                  onClick={saveHealth}
                  className="px-3 py-1 bg-pink-600 text-white rounded-md hover:bg-pink-700 flex items-center"
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-1" /> Enregistrer
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-semibold text-pink-800">État général:</span>
                  <span className="ml-2 text-pink-700">{client.healthSummary?.generalHealth || 'Non renseigné'}</span>
                </div>
                <div>
                  <span className="font-semibold text-pink-800">Pathologies:</span>
                  <span className="ml-2 text-pink-700">{client.healthSummary?.mainPathologies || 'Aucune'}</span>
                </div>
                <div>
                  <span className="font-semibold text-pink-800">Médicaments:</span>
                  <span className="ml-2 text-pink-700">{client.healthSummary?.medicationCount || '0'}</span>
                </div>
                <div>
                  <span className="font-semibold text-pink-800">Risque de chute:</span>
                  <span className="ml-2 text-pink-700">{client.healthSummary?.fallRisk || 'Non évalué'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section contacts d'urgence */}
      {(client.emergencyContacts && client.emergencyContacts.length > 0) || canEditClients ? (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Shield className="mr-2 text-red-600" size={20} />
              Contacts d'urgence
            </h2>
            {canEditClients && !isEditingContacts && (
              <button
                onClick={handleEditContacts}
                className="text-gray-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                title="Modifier les contacts d'urgence"
              >
                <Edit2 size={18} />
              </button>
            )}
          </div>

          {isEditingContacts ? (
            <div className="bg-white p-4 rounded-lg border-2 border-red-200 shadow-sm">
              <div className="space-y-4 mb-4">
                {contactsForm.map((contact, index) => (
                  <div key={index} className="p-3 bg-red-50 rounded-lg relative group">
                    <button
                      onClick={() => removeContact(index)}
                      className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        placeholder="Nom complet"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        className="p-2 border rounded text-sm"
                      />
                      <input
                        placeholder="Lien (ex: Fils, Voisin)"
                        value={contact.relationship}
                        onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                        className="p-2 border rounded text-sm"
                      />
                      <input
                        placeholder="Téléphone 1"
                        value={contact.phone1}
                        onChange={(e) => updateContact(index, 'phone1', e.target.value)}
                        className="p-2 border rounded text-sm"
                      />
                      <input
                        placeholder="Téléphone 2 (optionnel)"
                        value={contact.phone2}
                        onChange={(e) => updateContact(index, 'phone2', e.target.value)}
                        className="p-2 border rounded text-sm"
                      />
                    </div>
                  </div>
                ))}
                <button
                  onClick={addContact}
                  className="w-full py-2 border-2 border-dashed border-red-200 text-red-500 rounded-lg hover:bg-red-50 flex items-center justify-center font-medium"
                >
                  <Plus size={18} className="mr-2" /> Ajouter un contact
                </button>
              </div>
              <div className="flex justify-end space-x-2 border-t pt-4">
                <button
                  onClick={() => setIsEditingContacts(false)}
                  className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-md flex items-center"
                  disabled={isSaving}
                >
                  <X size={16} className="mr-1" /> Annuler
                </button>
                <button
                  onClick={saveContacts}
                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-1" /> Enregistrer
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {client.emergencyContacts && client.emergencyContacts.map((contact, index) => (
                <div key={index} className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-red-800">{contact.name}</h3>
                  <p className="text-sm text-red-700">{contact.relationship}</p>
                  <p className="text-sm text-red-600">📞 {contact.phone1}</p>
                  {contact.phone2 && (
                    <p className="text-sm text-red-600">📞 {contact.phone2}</p>
                  )}
                </div>
              ))}
              {(!client.emergencyContacts || client.emergencyContacts.length === 0) && (
                <p className="text-gray-500 italic">Aucun contact d'urgence renseigné</p>
              )}
            </div>
          )}
        </div>
      ) : null}
      {/* Section aidant familial */}
      {client.familyCaregiver && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Users className="mr-2 text-orange-600" size={20} />
            Aidant familial
          </h2>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-semibold text-orange-800">Nom:</span>
                <span className="ml-2 text-orange-700">{client.familyCaregiver.name}</span>
              </div>
              <div>
                <span className="font-semibold text-orange-800">Lien:</span>
                <span className="ml-2 text-orange-700">{client.familyCaregiver.relationship}</span>
              </div>
              <div>
                <span className="font-semibold text-orange-800">Fréquence:</span>
                <span className="ml-2 text-orange-700">{client.familyCaregiver.frequency}</span>
              </div>
              {client.familyCaregiver.burnoutLevel && (
                <div>
                  <span className="font-semibold text-orange-800">Charge:</span>
                  <span className="ml-2 text-orange-700">{client.familyCaregiver.burnoutLevel}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section des collaborateurs accessibles - Masquée pour les utilisateurs en lecture seule */}
      {(canEditClients || canViewContracts) && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4">Collaborateurs accessibles</h2>
          {accessibleEmployees.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accessibleEmployees.map((employee) => (
                <div key={employee.id} className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-green-800">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    <span className="bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full">
                      {employee.distance} km
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mb-2">
                    Zone: {employee.defaultRadius} km
                  </p>
                  {employee.address?.street && (
                    <p className="text-xs text-green-600">
                      📍 {employee.address.street}, {employee.address.postalCode} {employee.address.city}
                    </p>
                  )}
                  {employee.phoneNumber && (
                    <p className="text-xs text-green-600 mt-1">
                      📞 {employee.phoneNumber}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-gray-600 text-center">
                {employees.length > 0 ?
                  "Aucun collaborateur n'a ce client dans sa zone d'intervention" :
                  "Chargement des collaborateurs..."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSummary;
